import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react'
import { Turnstile, TurnstileInstance } from '@marsidev/react-turnstile'

// How long to wait for a token before letting the form submit without one.
// The API never blocks on Turnstile, so a blocked widget must not block signups.
export const TURNSTILE_TIMEOUT_MS = 4000

// The library injects the script once per page, under this id, and only tells
// the first widget when it fails to load. Every widget watches the tag itself.
const SCRIPT_ID = 'cf-turnstile-script'

export default function useTurnstile(action?: string) {
  const siteKey = process.env.TURNSTILE_SITE_KEY
  const id = useId()
  const instance = useRef<TurnstileInstance>(null)
  const [token, setToken] = useState<string>()
  const [gaveUp, setGaveUp] = useState(false)
  const [unavailable, setUnavailable] = useState(false)
  const active = !!action && !!siteKey && !unavailable

  useEffect(() => {
    if (!active) return

    const script = document.getElementById(SCRIPT_ID)
    if (!script) return
    if (script.dataset.failed) return setUnavailable(true)

    const onScriptError = () => {
      script.dataset.failed = 'true'
      setUnavailable(true)
    }
    script.addEventListener('error', onScriptError)
    return () => script.removeEventListener('error', onScriptError)
  }, [active])

  useEffect(() => {
    if (!active || token || gaveUp) return

    const timeout = setTimeout(() => setGaveUp(true), TURNSTILE_TIMEOUT_MS)
    return () => clearTimeout(timeout)
  }, [active, token, gaveUp])

  const clearToken = useCallback(() => setToken(undefined), [])

  const giveUp = useCallback(() => {
    setToken(undefined)
    setGaveUp(true)
  }, [])

  const markUnavailable = useCallback(() => setUnavailable(true), [])

  const scriptOptions = useMemo(
    () => ({ onError: markUnavailable }),
    [markUnavailable]
  )

  // Tokens are single-use, so the token is spent as soon as a submit starts:
  // a second click while the request is in flight waits for a fresh one.
  const withToken = useCallback(
    async <T,>(submit: (token?: string) => T | Promise<T>) => {
      setToken(undefined)
      setGaveUp(false)
      instance.current?.reset()
      return submit(token)
    },
    [token]
  )

  const widget = active ? (
    <Turnstile
      ref={instance}
      id={`cf-turnstile-${id}`}
      siteKey={siteKey!}
      options={{ size: 'invisible', action }}
      scriptOptions={scriptOptions}
      onSuccess={setToken}
      onExpire={clearToken}
      onError={giveUp}
      onUnsupported={markUnavailable}
    />
  ) : null

  return { isReady: !active || !!token || gaveUp, withToken, widget }
}
